import {  FormArray, FormControl, FormGroup } from '@angular/forms';
import { School } from '@models/school';
import { IUser, IUserUpdate } from '@models/user';
import { UserRole } from '@models/user-role';
import { RoleType } from '@shared/enums/role-type';
import { Helpers } from '@shared/helpers';
import { generalUserFields } from '@shared/manage-users/manage-users-form/constants/form-field-setup-data';
import { ParentRoleHelpers } from './parent-role-helpers';
import { StudentRoleHelpers } from './student-role-helpers';

/**
 * Mapping functions to map form data to the User data when editing an existing user.
 */
export namespace EditUserFormHelpers {

  /**
   * Maps form data to UserPartials with updated information for users that have updates.
   *
   * @param {IUser} originalUser User being edited.
   * @param {FormArray} usersEdited Any users being updated due to changes with the original User.
   * @param {string} schoolId School ID for current being managed
   * @param {RoleType} roleTypeEdited Role being edited.
   * @returns {IUserUpdate[]} Array of users being managed that have updates
   */
  export function mapEditUserRequestData(
    originalUser: IUser, usersEdited: FormArray,
    schoolId: string, roleTypeEdited: RoleType
  ): IUserUpdate[] {
    const usersToUpdate: IUserUpdate[] = [];
    // Update each User in the array. Some users are hidden (e.g. Parents added to a Student)
    usersEdited.controls.forEach((userFormGroup: FormGroup) => {
      if (roleTypeEdited === RoleType.Student) {
        const userAssociations = userFormGroup.get(generalUserFields.userAssociations.name) as FormArray;

        // Update new associations
        userAssociations.controls.forEach((association, parentIndex) => {
          const parentUserId = association.get(generalUserFields.parentUserId.name)?.value;
          const originalUserAssociation =  originalUser.userAssociations
            ?.find((user: IUser) => user.userId === parentUserId);

          // Add Association if an association does not exist, including new parent Users
          if (!originalUserAssociation) {
            const { classrooms } = originalUser;
            const parentUser = StudentRoleHelpers
              .addParentAsUserAndToAssociations(parentIndex, userFormGroup, schoolId, classrooms);
            if (parentUser) {
              usersToUpdate.push(parentUser);
            }
          }
        });

        // Remove User if parentUserId not in the userAssociations form array, but exists in original User associations
        originalUser.userAssociations?.forEach((association: IUser) => {
          const originalStillExistsInForm = userAssociations?.controls
            .find((associationControl: FormControl) => associationControl
              .get(generalUserFields.parentUserId.name)?.value === association.userId);
          if (!originalStillExistsInForm) {
            StudentRoleHelpers.removeParentAssociationFromStudent(originalUser, userFormGroup, association.userId);
          }
        });
      } else if (roleTypeEdited === RoleType.Parent) {
        // Update userAssociationsToRemove if a Student was removed
        const userAssociationsOnForm = userFormGroup.get(generalUserFields.userAssociations.name) as FormArray;
        originalUser.userAssociations.forEach((associationUser: IUser) => {
          const originalStillExistsInForm = userAssociationsOnForm?.controls
            .find((associationControl: FormControl) => associationControl
              .get('associatedUserId')?.value === associationUser.userId);
          if (!originalStillExistsInForm) {
            ParentRoleHelpers.addAssociationToRemove(originalUser, userFormGroup, associationUser.userId);
          }
        });
      }

      // Update user with all updated fields and any updated associations
      const updatedUser: IUserUpdate = {
        userId: originalUser.userId,
        firstName: userFormGroup.get(generalUserFields.firstName.name)?.value,
        lastName: userFormGroup.get(generalUserFields.lastName.name)?.value,
        ...userFormGroup.value,
      };

      // Remove password fields if password doesn't have value
      if (!updatedUser.confirmPassword) {
        // remove password keys if there is no password change
        delete updatedUser.password;
        delete updatedUser.confirmPassword;
      }

      // Apply the proper username for update
      Helpers.applyUsername(originalUser, updatedUser, userFormGroup.get(generalUserFields.username.name).value);

      // Apply password if given
      if (userFormGroup.get(generalUserFields.password.name)?.value) {
        Object.assign(updatedUser, { password: userFormGroup.get(generalUserFields.password.name).value });
      }

      usersToUpdate.push(updatedUser);
    });

    return usersToUpdate;
  }

    /**
   * Maps form data to UserPartials with updated information for users that have updates.
   *
   * @param {IUser} originalUser User being edited.
   * @param {FormArray} usersEdited Any users being updated due to changes with the original User.
   * @param {string} newSchoolId School ID where Student is being transferred.
   * @param {School[]}listOfSchools List of both schools involved in the transfer.
   * @param {string} originalSchoolId School ID where Student originated.
   * @returns {IUserUpdate[]} Array of users being managed that have updates
   */
  export function mapStudentTransferRequestData(
    originalUser: IUser, usersEdited: FormArray,
    newSchoolId: string, listOfSchools: School[],
    originalSchoolId: string
  ): IUserUpdate[] {
    const usersToUpdate: IUserUpdate[] = [];
    const newSchool = listOfSchools.find(s => s.educationalUnitId === newSchoolId);

    /* Update Each User In the Array - some users are hidden (parents added to students) */
    usersEdited.controls.forEach((userFormGroup: FormGroup) => {
      const userAssociations = userFormGroup.get(generalUserFields.userAssociations.name) as FormArray;

      userAssociations.controls.forEach((association, parentIndex) => {
        // Add Parent Association to new school, including existing and new parent Users
        const parentUser = StudentRoleHelpers
          .addParentAsUserAndToAssociations(parentIndex, userFormGroup, newSchoolId, null);
        if (parentUser) {
          usersToUpdate.push(parentUser);
        }
      });

      // Remove User if parentUserId not in the userAssociations form array, but exists in original User associations
      originalUser.userAssociations?.forEach((association: IUser) => {
        const originalStillExistsInForm = userAssociations.controls
          .find((associationControl: FormControl) => associationControl
            .get(generalUserFields.parentUserId.name)?.value === association.userId);
        if (!originalStillExistsInForm) {
          StudentRoleHelpers.removeParentAssociationFromStudent(originalUser, userFormGroup, association.userId);
        }
      });

      // Don't remove roles for the student unless transferring to a new school.
      let rolesToRemove: UserRole[] = [];

      const rolesToAdd = [{
        educationalUnitId: newSchool.educationalUnitId,
        roleType: RoleType.Student
      }] as UserRole[];

      // If transferring to a new school, remove the memberships.
      if (newSchool.educationalUnitId !== originalSchoolId) {
        rolesToRemove = [].concat(originalUser.schools).concat(originalUser.classrooms).map(r => ({
          educationalUnitId: r.educationalUnitId ? r.educationalUnitId : r.schoolId,
          classroomId: r.classroomId,
          roleType: r.roleType
        }) as UserRole);
      }

      const newStudentUsername = `${userFormGroup.get(generalUserFields.username.name).value}_${newSchool.abbreviatedSchoolId}`;

      // Update student user
      const updatedUser: IUserUpdate = {
        userId: originalUser.userId,
        firstName: userFormGroup.get(generalUserFields.firstName.name)?.value,
        lastName: userFormGroup.get(generalUserFields.lastName.name)?.value,
        ...userFormGroup.value,
        rolesToAdd,
        rolesToRemove,
        username: newStudentUsername
      };

      // Remove password fields if password doesn't have value
      if (!updatedUser.confirmPassword) {
        // remove password keys if there is no password change
        delete updatedUser.password;
        delete updatedUser.confirmPassword;
      }

      // Apply password if given
      if (userFormGroup.get(generalUserFields.password.name)?.value) {
        Object.assign(updatedUser, { password: userFormGroup.get(generalUserFields.password.name).value });
      }

      usersToUpdate.push(updatedUser);
    });

    return usersToUpdate;
  }
}
